import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Copy, Gift, Users, ArrowRight } from "lucide-react";
import { useTr } from "@/i18n/translator";

export default function Referrals() {
  const tr = useTr();
  const [info, setInfo] = useState(null);

  useEffect(() => { api.get("/referrals/me").then(({ data }) => setInfo(data)); }, []);

  const link = info ? `${window.location.origin}/register?ref=${info.code}` : "";

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copiado al portapapeles");
  };

  if (!info) return <div className="font-mono-mark text-xs text-[#5C5C5C]">CARGANDO…</div>;

  return (
    <div className="space-y-6" data-testid="referrals-page">
      <div>
        <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("PROGRAMA · REFERIDOS")}</div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2">{tr("Invita y gana Pro gratis")}</h1>
        <p className="mt-3 font-mono-data text-sm text-[#5C5C5C] max-w-2xl">
          {tr("Cada amigo que se registre con tu código, ambos ganan")} <b className="text-[#E53935]">{tr("30 días de Pro")}</b> sin costo.
          Sin límite de invitaciones. Los bonos se acumulan.
        </p>
      </div>

      {/* Hero Code Card */}
      <div className="bg-[#0A0A0A] text-white border border-[#0A0A0A] brutal-shadow-red p-8">
        <div className="font-mono-mark text-[10px] text-[#FFD600]">{tr("TU CÓDIGO")}</div>
        <div className="mt-3 font-display text-5xl md:text-6xl tracking-tighter" data-testid="ref-code">{info.code}</div>
        <div className="mt-6 flex flex-wrap gap-3">
          <button onClick={() => copy(info.code)} className="font-mono-mark text-[10px] bg-[#FFD600] text-[#0A0A0A] px-4 py-3 border border-[#FFD600] hover:bg-white flex items-center gap-2" data-testid="ref-copy-code">
            <Copy className="w-4 h-4" /> {tr("COPIAR CÓDIGO")}
          </button>
          <button onClick={() => copy(link)} className="font-mono-mark text-[10px] bg-[#E53935] text-white px-4 py-3 border border-[#E53935] flex items-center gap-2" data-testid="ref-copy-link">
            <ArrowRight className="w-4 h-4" /> {tr("COPIAR LINK DE INVITACIÓN")}
          </button>
        </div>
        <div className="mt-6 font-mono-data text-xs text-white/60 break-all">{link}</div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-[#0A0A0A]">
        <div className="p-6 bg-white flex items-center gap-4">
          <Users className="w-8 h-8 text-[#E53935]" />
          <div>
            <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("INVITADOS EXITOSOS")}</div>
            <div className="font-display text-4xl mt-1 font-mono-data" data-testid="ref-count">{info.count}</div>
          </div>
        </div>
        <div className="p-6 bg-white md:border-l border-[#0A0A0A] flex items-center gap-4">
          <Gift className="w-8 h-8 text-[#E53935]" />
          <div>
            <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("DÍAS PRO BONUS")}</div>
            <div className="font-display text-4xl mt-1 font-mono-data" data-testid="ref-days">{info.bonus_days_remaining}</div>
          </div>
        </div>
        <div className="p-6 bg-[#FFD600] md:border-l border-[#0A0A0A] flex items-center gap-4">
          <div>
            <div className="font-mono-mark text-[10px]">HASTA</div>
            <div className="font-display text-2xl mt-1 font-mono-data" data-testid="ref-until">
              {info.bonus_until ? new Date(info.bonus_until).toLocaleDateString("es-MX") : "—"}
            </div>
          </div>
        </div>
      </div>

      {/* How it works */}
      <div className="bg-white border border-[#0A0A0A] p-6 md:p-8">
        <div className="font-mono-mark text-[10px] text-[#5C5C5C]">{tr("PROTOCOLO · CÓMO FUNCIONA")}</div>
        <div className="grid md:grid-cols-3 gap-6 mt-4">
          {[
            ["01", "Compartes tu código", "Cópialo y mándalo a tus contactos, o comparte el link directo."],
            ["02", "Tu amigo se registra", "Al crear cuenta pone el código CIA-XXXXXX en el campo de referido."],
            ["03", "Ambos ganan 30 días Pro", "El bonus se activa automáticamente. Se acumulan por cada invitado."],
          ].map(([n, title, d]) => (
            <div key={n}>
              <div className="font-display text-5xl text-[#E53935] leading-none">{n}</div>
              <div className="font-display text-xl mt-3">{tr(title)}</div>
              <p className="font-mono-data text-sm text-[#5C5C5C] mt-1">{tr(d)}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
